import * as Sentry from '@sentry/react'
import type { ErrorEvent, EventHint } from '@sentry/react'

const beforeSend = (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
  const environment = process.env.REACT_APP_ENV || 'development'

  // Add debug logging in development
  if (environment === 'development') {
    console.log('🔍 Sentry beforeSend:', { // eslint-disable-line no-console
      eventId: event.event_id,
      message: event.message || (event.exception?.values?.[0]?.value),
      tags: event.tags,
      extra: event.extra,
      timestamp: new Date().toISOString()
    })
  }

  if (event.user) {
    const { email, username, ip_address, id, ...userWithoutPII } = event.user
    event.user = userWithoutPII
  }

  if (event.request?.url) {
    try {
      const url = new URL(event.request.url)
      url.search = ''
      event.request.url = url.toString()
    } catch (e) {
      const { url: removedUrl, ...requestWithoutUrl } = event.request
      event.request = requestWithoutUrl
    }
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(breadcrumb => ({
      ...breadcrumb,
      data: undefined,
    }))
  }

  return event
}

export function initSentry(): void {
  const environment = process.env.REACT_APP_ENV || 'development'
  const dsn = process.env.REACT_APP_SENTRY_DSN

  if (!dsn) {
    console.warn('Sentry DSN not provided. Sentry will not be initialized.') // eslint-disable-line no-console
    return
  }

  Sentry.init({
    dsn,
    environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.0,
    beforeSend,
    sendDefaultPii: false,
    // Disable rate limiting in development for testing
    ...(environment === 'development' && {
      // Allow unlimited events in development
      maxBreadcrumbs: 100,
      // Custom transport options to bypass rate limiting
      transportOptions: {
        // Remove default rate limiting
      },
    }),
    release: process.env.REACT_APP_VERSION_NUMBER || 'unknown',
    beforeSendTransaction: (transaction) => {
      if (transaction.transaction?.includes('user') || transaction.transaction?.includes('profile')) {
        return null
      }
      return transaction
    },
    initialScope: {
      tags: {
        component: 'tempo-screens',
      },
    },
  })

  // Override rate limiting for development
  if (environment === 'development') {
    const client = Sentry.getClient()
    if (client) {
      // Override the client's rate limiter for development
      ;(client as any)._rateLimiter = {
        isRateLimited: () => false,
        recordSuccess: () => {},
        recordError: () => {},
      }
      console.log('🚀 Sentry rate limiting disabled for development') // eslint-disable-line no-console
    }
  }

  console.log(`Sentry initialized for environment: ${environment}`) // eslint-disable-line no-console
}

interface ContextData {
  [key: string]: any
}

export function captureException(error: Error, context?: ContextData): void {
  const environment = process.env.REACT_APP_ENV || 'development'

  // In development, force bypass rate limiting before every capture
  if (environment === 'development') {
    const client = Sentry.getClient()
    if (client && (client as any)._rateLimiter) {
      ;(client as any)._rateLimiter.isRateLimited = () => false
    }
  }

  Sentry.withScope((scope) => {
    if (context) {
      const piiKeys = ['email', 'username', 'userid', 'id', 'phone', 'address']

      const sanitizedContext = Object.fromEntries(
        Object.entries(context).filter(([key]) => {
          const isFiltered = piiKeys.includes(key.toLowerCase())
          return !isFiltered
        })
      )

      scope.setContext('additional', sanitizedContext)
    }

    // In development, bypass deduplication with unique tags and force unique fingerprint
    if (environment === 'development') {
      const timestamp = Date.now().toString()
      const random = Math.random().toString(36).substring(2, 8)

      scope.setTag('dev_timestamp', timestamp)
      scope.setTag('dev_random', random)

      // Force unique fingerprint to bypass all deduplication
      scope.setFingerprint([
        error.message || 'unknown-error',
        timestamp,
        random,
        'tempo-screens-dev'
      ])

      console.log(`📤 Sending error to Sentry (tempo_screens) - timestamp: ${timestamp}, random: ${random}`) // eslint-disable-line no-console
    }

    Sentry.captureException(error)
  })

  // In development, try multiple times if rate limited
  if (environment === 'development') {
    // Retry after a short delay in case the first attempt was rate limited
    setTimeout(() => {
      const client = Sentry.getClient()
      if (client && (client as any)._rateLimiter && (client as any)._rateLimiter.isRateLimited()) {
        console.log('🔄 Retrying Sentry capture due to rate limiting...') // eslint-disable-line no-console
        Sentry.captureException(error)
      }
    }, 50)
  }
}

interface ExtraData {
  [key: string]: any
}

export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info', extra?: ExtraData): void {
  Sentry.withScope((scope) => {
    if (extra) {
      scope.setExtra('details', extra)
    }
    Sentry.captureMessage(message, level)
  })
}
